/**
 * One-way bridge: AutoCAD `SELECTIONPREVIEW` / `PREVIEWEFFECT` /
 * `PREVIEWFILTER` / `ROLLOVERTIPS` sysvar writes → `previewPreferences`
 * zustand store.
 *
 * # Why a bridge module
 *
 * The renderer + ToolTipCard read from `previewPreferences` (which is
 * persisted to `localStorage` under `edit-dwg.preview`). The sysvar
 * store (`useSysvarStore`) is the source of truth for SETVAR /
 * Options-dialog interactions. Keeping the two in sync without making
 * one depend on the other internally is the cleanest design — both
 * stores stay testable in isolation, and a single subscription wires
 * them together at app boot.
 *
 * # Direction
 *
 * Sysvar → previewPreferences only. The Options dialog already writes
 * directly to `previewPreferences`; reverse-bridging would create a
 * write loop. Instead, the dialog should also push changes into the
 * sysvar store via `useSysvarStore.getState().setVar(...)` when it
 * mutates a value (Phase 2 wire — out of scope for B17). For now the
 * dialog mutates only the prefs store; the sysvar slot drifts unless
 * the user explicitly issues SETVAR ROLLOVERTIPS 0 etc., which is
 * acceptable — AutoCAD itself does not perfectly mirror dialog state
 * to the active sysvar unit until the dialog is dismissed.
 *
 * # Lifecycle
 *
 * Call `installPreviewSysvarBridge()` once at App boot. The function
 * returns a teardown closure; the App.tsx mount effect runs it on
 * unmount so HMR replays don't pile up subscribers.
 */

import { useSysvarStore } from "../dynamicInput/sysvarStore";
import { getPreviewPreferencesApi } from "./previewPreferences";

/**
 * Subscribe to sysvar writes and forward the four hover-related slots
 * into the previewPreferences store. Returns a teardown closure.
 *
 * Direction is one-way: sysvar → previewPreferences. Reverse-bridging
 * would create a write loop with the Options dialog, which already
 * mutates `previewPreferences` directly.
 */
export function installPreviewSysvarBridge(): () => void {
  // Resolve setters once at install — zustand setter identities are
  // stable. The bridge keeps a single subscription on the sysvar store.
  const prefs = getPreviewPreferencesApi();

  return useSysvarStore.subscribe((next, prev) => {
    if (next.values === prev.values) return;
    if (next.values.SELECTIONPREVIEW !== prev.values.SELECTIONPREVIEW) {
      const v = next.values.SELECTIONPREVIEW;
      if (v === 0 || v === 1 || v === 2 || v === 3) {
        prefs.setSelectionPreview(v);
      }
    }
    if (next.values.PREVIEWEFFECT !== prev.values.PREVIEWEFFECT) {
      const v = next.values.PREVIEWEFFECT;
      if (v === 0 || v === 1 || v === 2) {
        prefs.setPreviewEffect(v);
      }
    }
    if (next.values.PREVIEWFILTER !== prev.values.PREVIEWFILTER) {
      prefs.setPreviewFilter(next.values.PREVIEWFILTER);
    }
    if (next.values.ROLLOVERTIPS !== prev.values.ROLLOVERTIPS) {
      const v = next.values.ROLLOVERTIPS;
      if (v === 0 || v === 1) {
        prefs.setRolloverTips(v);
      }
    }
  });
}
